import { Box, Skeleton, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import { DocxDisplay } from "./DocxDisplay";
import { ismsWordMimeTypes } from "./getDocumentExtension";
import { ImageDisplay } from "./ImageDisplay";
import { PdfDisplay } from "./PdfDisplay";

interface UrlDisplayProps {
  url: string;
}

export function UrlDisplay(props: UrlDisplayProps) {
  const { url } = props;
  const { data, isSuccess, isLoading } = useQuery({
    queryKey: [url],
    queryFn: async (): Promise<[string, Blob]> => {
      const response = await axios.get(url, {
        responseType: "blob",
      });
      return [response.headers["content-type"], response.data];
    },
    staleTime: Number.POSITIVE_INFINITY,
    cacheTime: Number.POSITIVE_INFINITY,
  });

  const renderContentType = ([contentType, blob]: [string, Blob]) => {
    if (ismsWordMimeTypes(contentType)) {
      return <DocxDisplay url={url} />;
    }

    if (contentType.startsWith("image/")) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <ImageDisplay imageUrl={url} />
        </Box>
      );
    }

    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line n/no-unsupported-features/node-builtins
    return <PdfDisplay documentUrl={URL.createObjectURL(blob)} defaultScale={1} />;
  };

  return (
    <>
      {isLoading && (
        <Stack spacing={1.5}>
          <Stack alignItems="center" direction="row" spacing={2} justifyContent="center">
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="circular" width={24} height={24} />
          </Stack>
          <Skeleton variant="rounded" animation="wave" height={48} />
        </Stack>
      )}
      {isSuccess ? renderContentType(data) : null}
    </>
  );
}
